import {
  Check,
  Clock,
  EyeOff,
  Loader2,
  OctagonX,
  RefreshCw,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { SystemId } from "@/components/ui/system-id";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useEntitiesByIds } from "@/hooks/api/use-entities";
import {
  type MilestoneAnalysis,
  type MilestoneAnalysisOtherCitation,
  type MilestoneAnalysisTouchpoint,
  type TaskGroup,
  useRetryMilestoneAnalysisAnalysis,
  useUpdateMilestoneAnalysisStatus,
  useUpdateMilestoneAnalysisTask,
} from "@/hooks/api/use-milestone-analyses";
import { formatDate } from "@/lib/date-formatting";
import { HistoryDialog } from "@/pages/accounts/account-explorer/milestone-analyses/history-dialog";
import { formatSnakeCase } from "@/utils/formatters";

interface MilestoneAnalysesheetProps {
  taskGroup: TaskGroup;
  onFilterCitedTouchpoints?: (
    taskId: string,
    touchpoints: MilestoneAnalysisTouchpoint[],
  ) => void;
  hasCitedTouchpointsFilter: boolean;
  selectedAnalysis: MilestoneAnalysis;
  setSelectedAnalysis: (milestoneAnalysis: MilestoneAnalysis) => void;
}

function TouchpointItem({
  touchpoint,
}: {
  touchpoint: MilestoneAnalysisTouchpoint;
}) {
  return (
    <div className="py-4 border-b">
      <div className="grid grid-cols-[80px_1fr_auto] items-baseline gap-1 mb-4">
        <span className="text-sm text-muted-foreground font-medium">
          Activity:
        </span>
        <span className="text-sm font-medium">
          {touchpoint.inferred_from_id
            ? touchpoint.detail
            : touchpoint.activity}
        </span>
        <span className="text-xs text-muted-foreground justify-self-end">
          {formatDate(touchpoint.date)}
        </span>
      </div>

      <div className="grid grid-cols-[80px_1fr] items-baseline gap-1 mb-4">
        <span className="text-sm text-muted-foreground font-medium">
          Channel:
        </span>
        <div>
          {!touchpoint.inferred_from_id && <Badge>{touchpoint.channel}</Badge>}
        </div>
      </div>

      {touchpoint.reason && (
        <div className="grid grid-cols-[80px_1fr] gap-1 mb-4">
          <span className="text-sm text-muted-foreground font-medium">
            Citation Reason:
          </span>
          <span className="text-sm">{touchpoint.reason}</span>
        </div>
      )}

      {touchpoint.detail && (
        <div className="grid grid-cols-[80px_1fr] gap-1">
          <span className="text-sm text-muted-foreground font-medium">
            Detail:
          </span>
          {touchpoint.inferred_from_id ? (
            <div className="text-sm">
              <Badge
                variant="secondary"
                className="mr-2 mb-1 text-xs bg-supporting-purple/10 text-supporting-purple hover:bg-supporting-purple/10"
              >
                INFERRED FROM
              </Badge>
              <span className="text-sm">{touchpoint.activity}</span>
            </div>
          ) : (
            <div className="text-sm">{touchpoint.detail}</div>
          )}
        </div>
      )}

      <div className="flex justify-end mt-2">
        <SystemId id={touchpoint.id || touchpoint.inferred_from_id || "N/A"} />
      </div>
    </div>
  );
}

function OtherCitationItem({
  citation,
  entityName,
  entityType,
}: {
  citation: MilestoneAnalysisOtherCitation;
  entityName: string;
  entityType?: string;
}) {
  return (
    <div className="py-4 border-b">
      <div className="grid grid-cols-[80px_1fr] items-baseline gap-1 mb-4">
        <span className="text-sm text-muted-foreground font-medium">
          Entity:
        </span>
        <div className="flex gap-1">
          {entityType && (
            <Badge variant="secondary" className="text-xs inline-flex w-fit">
              {entityType}
            </Badge>
          )}
          <span className="text-sm font-medium">{entityName}</span>
        </div>
      </div>

      <div className="grid grid-cols-[80px_1fr] items-baseline gap-1 mb-4">
        <span className="text-sm text-muted-foreground font-medium">
          Field:
        </span>
        <span className="text-sm">{citation.field}</span>
      </div>

      <div className="grid grid-cols-[80px_1fr] gap-1 mb-4">
        <span className="text-sm text-muted-foreground font-medium">
          Detail:
        </span>
        <span className="text-sm">{citation.detail}</span>
      </div>

      <div className="grid grid-cols-[80px_1fr] gap-1">
        <span className="text-sm text-muted-foreground font-medium">
          Reason:
        </span>
        <span className="text-sm">{citation.reason}</span>
      </div>

      <div className="flex justify-end mt-2">
        <SystemId id={citation.id || "N/A"} />
      </div>
    </div>
  );
}

export function MilestoneAnalysesSheet({
  taskGroup,
  onFilterCitedTouchpoints,
  hasCitedTouchpointsFilter,
  selectedAnalysis,
  setSelectedAnalysis,
}: MilestoneAnalysesheetProps) {
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [instructions, setInstructions] = useState("");

  const updateStatusMutation = useUpdateMilestoneAnalysisStatus();
  const regenerateMutation = useUpdateMilestoneAnalysisTask();
  const retryMutation = useRetryMilestoneAnalysisAnalysis();
  const posthog = usePostHog();

  // Sort analyses by last_updated, newest first
  const sortedAnalyses = [...taskGroup.analyses].sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
  );

  // Convert touchpoints for display
  const displayTouchpoints = useMemo(
    () => selectedAnalysis?.touchpoint_citations || [],
    [selectedAnalysis],
  );

  // Convert metadata evidence for display
  const displayMetadataEvidence = useMemo(
    () => selectedAnalysis?.other_citations || [],
    [selectedAnalysis],
  );

  // Extract entity IDs from metadata evidence
  const entityIds = useMemo(() => {
    return displayMetadataEvidence.map((item) => item.id).filter(Boolean);
  }, [displayMetadataEvidence]);

  // Fetch entities data on component load
  const { data: entitiesMap, isLoading: isLoadingEntities } =
    useEntitiesByIds(entityIds);

  // Handle regenerate
  const handleRegenerate = async () => {
    await regenerateMutation.mutateAsync(
      {
        task_id: taskGroup.task_id,
        extra_instructions: instructions,
      },
      {
        onSuccess: () => {
          posthog?.capture("analysis_task_regenerated", {
            analysis_type: taskGroup.analysis_type,
            task_id: taskGroup.task_id,
            opportunity_id: taskGroup.opportunity_id,
            opportunity_name: selectedAnalysis?.opportunity_name,
            has_extra_instructions: !!instructions,
            context: "side_sheet",
          });
        },
      },
    );
    setShowRegenerateDialog(false);
    setInstructions("");
  };

  // Handle filter touchpoints
  const handleFilterTouchpoints = () => {
    if (onFilterCitedTouchpoints && displayTouchpoints.length > 0) {
      onFilterCitedTouchpoints(taskGroup.task_id, displayTouchpoints);
    }
  };

  // Function to get entity display name
  const getEntityDisplay = (entityId: string) => {
    if (isLoadingEntities) return "Loading...";

    const entity = entitiesMap?.[entityId];
    if (!entity) return entityId;

    return entity.name || entityId;
  };

  // For non-admin users, hide unapproved analyses entirely
  // Non-admin check is handled by parent (it passes selectedAnalysis appropriately).
  return (
    <div className="flex flex-col h-full">
      {/* Fixed Header */}
      <div className="flex flex-col gap-4 pb-4">
        <div className="flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold">
              {selectedAnalysis
                ? `${selectedAnalysis.opportunity_name} | ${formatSnakeCase(
                    selectedAnalysis.analysis_type,
                  )}`
                : "Loading analysis..."}
            </h2>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex gap-1">
              {sortedAnalyses.length > 1 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShowHistoryDialog(true)}
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View previous analyses ({sortedAnalyses.length})</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {taskGroup.processing_status !== "failed" && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShowRegenerateDialog(true)}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Regenerate analysis</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {taskGroup.processing_status === "failed" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1 text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => {
                    retryMutation.mutate(
                      {
                        task_id: taskGroup.task_id,
                      },
                      {
                        onSuccess: () => {
                          posthog?.capture("analysis_task_retried", {
                            analysis_type: taskGroup.analysis_type,
                            task_id: taskGroup.task_id,
                            opportunity_id: taskGroup.opportunity_id,
                            opportunity_name:
                              selectedAnalysis?.opportunity_name,
                            error_message: taskGroup.error_message?.slice(
                              0,
                              100,
                            ), // Send just the beginning of the error
                            context: "side_sheet",
                          });
                        },
                      },
                    );
                  }}
                  disabled={retryMutation.isPending}
                >
                  <OctagonX className="h-4 w-4" />
                  {retryMutation.isPending ? (
                    <span className="flex items-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      Retrying...
                    </span>
                  ) : (
                    <span>Failed - Retry</span>
                  )}
                </Button>
              )}
              {/* Non-Published Version Indicator with Action */}
              {selectedAnalysis &&
                !selectedAnalysis.is_active_version &&
                taskGroup.processing_status !== "failed" && (
                  <div className="flex items-center mr-2 p-1 bg-warning/10 rounded border border-warning overflow-hidden">
                    <div className="flex items-center px-2 py-1">
                      <EyeOff className="h-3 w-3 mr-1 text-warning" />
                      <span className="text-xs text-warning">
                        Unpublished version
                      </span>
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 py-1 rounded-none border-l border-warning text-warning hover:bg-warning/10 hover:text-warning"
                          onClick={() => {
                            updateStatusMutation.mutate(
                              {
                                analysisId: selectedAnalysis.analysis_id,
                                isApproved: true,
                                isActiveVersion: true,
                              },
                              {
                                onSuccess: () => {
                                  posthog?.capture("analysis_published", {
                                    analysis_id: selectedAnalysis.analysis_id,
                                    analysis_type:
                                      selectedAnalysis.analysis_type,
                                    opportunity_id: taskGroup.opportunity_id,
                                    opportunity_name:
                                      selectedAnalysis.opportunity_name,
                                    task_id: taskGroup.task_id,
                                    context: "side_sheet",
                                  });
                                },
                              },
                            );
                          }}
                        >
                          Publish
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Make this the currently active version</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              {/* Published Version Indicator */}
              {selectedAnalysis?.is_active_version &&
                taskGroup.processing_status !== "failed" && (
                  <div className="flex items-center mr-2 p-1 bg-success/10 rounded border border-success overflow-hidden gap-2">
                    <div className="flex items-center px-2 py-1">
                      <Check className="h-3 w-3 mr-1 text-success" />
                      <span className="text-xs text-success">Published</span>
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 py-1 rounded-none border-l border-success text-success hover:bg-success/10 hover:text-success"
                          onClick={() => {
                            updateStatusMutation.mutate(
                              {
                                analysisId: selectedAnalysis.analysis_id,
                                isApproved: true,
                                isActiveVersion: false,
                              },
                              {
                                onSuccess: () => {
                                  posthog?.capture("analysis_unpublished", {
                                    analysis_id: selectedAnalysis.analysis_id,
                                    analysis_type:
                                      selectedAnalysis.analysis_type,
                                    opportunity_id: taskGroup.opportunity_id,
                                    opportunity_name:
                                      selectedAnalysis.opportunity_name,
                                    task_id: taskGroup.task_id,
                                    context: "side_sheet",
                                  });
                                },
                              },
                            );
                          }}
                        >
                          Unpublish
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Unpublish this version</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Status Indicators */}
        {taskGroup.processing_status === "failed" ? (
          <div className="text-sm bg-destructive/10 p-3 rounded text-destructive">
            <p className="font-medium mb-1">Analysis failed</p>
            <p>An error occurred during analysis.</p>
          </div>
        ) : taskGroup.processing_status === "processing" ? (
          <div className="text-sm bg-primary/10 p-3 rounded">
            <p>Analysis in progress...</p>
          </div>
        ) : null}
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto pr-4 flex-1">
        {/* Analysis Content */}
        <div className="mb-4">
          {selectedAnalysis?.opinion?.opinion && (
            <h3 className="text-md font-semibold mb-2">
              {selectedAnalysis.opinion?.opinion}
            </h3>
          )}
          <p className="text-sm text-muted-foreground">
            {selectedAnalysis?.summary}
          </p>
        </div>

        <Separator className="my-4" />

        {/* Touchpoints Section */}
        {displayTouchpoints.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium">
                Cited touchpoints ({displayTouchpoints.length})
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFilterTouchpoints}
              >
                Highlight in table
              </Button>
            </div>
            <div className="mt-2 space-y-1 pl-2">
              {displayTouchpoints.map((touchpoint) => (
                <TouchpointItem key={touchpoint.id} touchpoint={touchpoint} />
              ))}
            </div>
          </div>
        )}

        {/* Metadata Evidence Section */}
        {displayMetadataEvidence.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium">
                Other citations ({displayMetadataEvidence.length})
              </div>
            </div>
            <div className="mt-2 space-y-1 pl-2">
              {displayMetadataEvidence.map((item) => (
                <OtherCitationItem
                  key={`${item.id}-${item.field}`}
                  citation={item}
                  entityName={getEntityDisplay(item.id)}
                  entityType={entitiesMap?.[item.id]?.type}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Regenerate Dialog */}
      <Dialog
        open={showRegenerateDialog}
        onOpenChange={setShowRegenerateDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate this summary</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="(Optional) Provide additional instructions for the agent to consider."
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRegenerateDialog(false)}
              disabled={regenerateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={regenerateMutation.isPending}
            >
              {regenerateMutation.isPending ? "Regenerating..." : "Regenerate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <HistoryDialog
        open={showHistoryDialog}
        onOpenChange={setShowHistoryDialog}
        sortedAnalyses={sortedAnalyses}
        onAnalysisChange={(newAnalysis) => {
          setSelectedAnalysis(newAnalysis);
          if (hasCitedTouchpointsFilter && onFilterCitedTouchpoints) {
            onFilterCitedTouchpoints(
              taskGroup.task_id,
              newAnalysis.touchpoint_citations || [],
            );
          }
        }}
      />
    </div>
  );
}
